import React, { useMemo } from "react";
import { faRocket, faBook } from "@fortawesome/free-solid-svg-icons";
import {
  DevToolsProvider,
  amplitudePlugin,
  envHealthPlugin,
  mobiusLinkPlugin,
  tracesPlugin,
  idmPlugin,
  featureFlagsPlugin,
  toolGroup,
  separator,
  linkOut,
  type DevToolsItem,
} from "components/devtools";
import { journeyGroup } from "components/devtools/plugins/groups/journey";

interface Site {
  name: string;
  description: string;
  href: string;
}

interface Variant {
  key: string;
  value: string;
  isControl: boolean;
  isActive: boolean;
}

interface Experiment {
  name: string;
  label: string;
  variants: Variant[];
  hasActiveVariant: boolean;
}

interface DevToolsProps {
  // From DevToolsViewModel
  mainSites: Site[];
  ukSites: Site[];
  usSites: Site[];
  verticals: Record<string, string>;
  domainUserId?: string;
  currentSite: string;
  journeyId?: string;
  verticalForIdmRequest?: string;
  amplitudeExperiments: string[];
  amplitudeExperimentsDetailed?: {
    currentPage: Experiment[];
    allOthers: Experiment[];
  };
  // From AutoFillViewModel
  answerSets?: Record<string, [string, string][]>;
  site?: string;
  fillJourneyAnswersPath?: string;
  fillCustomJourneyAnswersPath?: string;
  isDevelopment?: boolean;
  isLocalDevelopment?: boolean;
  // Seeded from the chopin-devtools-sidebar cookie so SSR paints the right state
  initialSidebarExpanded?: boolean;
  // Link-out targets resolved from the named Rails routes (DevToolsViewModel), so
  // a route rename does not silently orphan a hardcoded path here.
  newBusinessShowcaseHref?: string;
  apiDocsHref?: string;
}

function DevTools(props: DevToolsProps) {
  const items = useMemo<DevToolsItem[]>(
    () => [
      journeyGroup({
        ...props,
        site: props.site || props.currentSite,
      }),
      ...(props.isDevelopment ? [mobiusLinkPlugin()] : []),
      separator(),
      toolGroup("Experiments", [
        amplitudePlugin({ ...props }),
        idmPlugin({
          ...props,
          verticalForIdmRequest: props.verticalForIdmRequest?.toLowerCase(),
        }),
        featureFlagsPlugin(),
      ]),
      separator(),
      toolGroup("Diagnostics", [
        tracesPlugin(),
        ...(props.isLocalDevelopment ? [envHealthPlugin()] : []),
      ]),
      separator(),
      // Standalone New Business v0 API dev pages. Both are gated to
      // non-production + the internal network (same envs DevTools shows in), so
      // they are always reachable here - surface them so engineers don't have to
      // know the URLs. Hrefs come from the named routes via DevToolsViewModel; the
      // literal is a fallback for when props are absent (all_props rescues to {}).
      linkOut({
        id: "new-business-showcase",
        name: "New Business Showcase",
        icon: faRocket,
        href: props.newBusinessShowcaseHref ?? "/new-business-showcase",
      }),
      linkOut({
        id: "api-docs",
        name: "API Docs",
        icon: faBook,
        href: props.apiDocsHref ?? "/api-docs",
      }),
    ],
    // Props come from server-rendered data and don't change within a page lifecycle
    [],
  );

  return (
    <DevToolsProvider
      items={items}
      initialSidebarExpanded={props.initialSidebarExpanded}
    />
  );
}

export default DevTools;
